import { useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Lock, ArrowLeft, LogIn, ShieldCheck } from "lucide-react";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import CaptchaField from "../components/CaptchaField";
import SecurityAlert from "../components/SecurityAlert";

const inputCls = "w-full bg-white border border-slate-300 rounded-md px-4 py-3 text-sm text-[#0B1220] placeholder:text-slate-400 focus:border-[#5698C7] focus:outline-none focus:ring-1 focus:ring-[#5698C7] transition-colors duration-200 shadow-sm";

export default function Login() {
  const { t } = useLang();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [alerted, setAlerted] = useState(false);
  const captchaRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!captchaToken) { setError("Completa la verificación reCAPTCHA"); return; }
    setError(""); setLoading(true);
    try {
      const user = await login(email, password, captchaToken);
      navigate(user.role === "admin" ? "/matriz" : "/intranet");
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) setAlerted(true);
      else if (status === 403 && String(err.response?.data?.detail || "").includes("PIN")) {
        sessionStorage.removeItem("nc_pin_token");
        navigate("/", { replace: true });
        return;
      } else setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      if (captchaToken !== "disabled") { setCaptchaToken(""); captchaRef.current?.reset(); }
    } finally { setLoading(false); }
  };

  if (!sessionStorage.getItem("nc_pin_token")) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center relative overflow-hidden px-4" data-testid="login-page">
      <div className="hero-orb light-orb w-[700px] h-[700px] opacity-40" />
      <div className="hero-orb light-orb w-[500px] h-[500px] opacity-30" style={{ animationDirection: "reverse" }} />
      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-xs font-mono-tech text-slate-500 hover:text-[#5698C7] mb-8 transition-colors duration-200" data-testid="login-back-link">
          <ArrowLeft size={12} /> NATURCOMMODITIES.COM
        </Link>
        <div className="hud-border bg-white/90 backdrop-blur-2xl p-10 glow-blue">
          <div className="w-10 h-10 border border-[#5698C7] flex items-center justify-center mb-6">
            <Lock size={16} className="text-[#5698C7]" strokeWidth={1.5} />
          </div>
          <h1 className="font-display text-xl font-light mb-2" data-testid="login-title">{t("login_title")}</h1>
          <p className="font-mono-tech text-[10px] tracking-widest text-slate-500 mb-8">MATRIZ // INTRANET</p>
          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-sm text-[#0B1220] mb-2">User data</label>
              <input required type="text" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="xxxxxxxx" className={inputCls} data-testid="login-email-input" />
            </div>
            <div>
              <label className="block text-sm text-[#0B1220] mb-2">Authorization Code</label>
              <input required type="password" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="login-password-input" />
            </div>
            {alerted && <SecurityAlert />}
            {error && <p className="text-xs text-red-600 font-mono-tech" data-testid="login-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="login-form-submit-button"
              className="w-full bg-[#5698C7] text-white font-mono-tech text-xs tracking-widest font-medium py-3 rounded-md hover:bg-[#4587B8] transition-colors duration-200 disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {loading ? "..." : <><LogIn size={13} /> ENTRAR</>}
            </button>
            <div className="flex justify-center"><CaptchaField captchaRef={captchaRef} onToken={setCaptchaToken} /></div>
            {captchaToken !== "disabled" && (
            <p className="text-center text-[11px] text-slate-400 flex items-center justify-center gap-1.5" data-testid="login-recaptcha-note">
              <ShieldCheck size={12} className="text-emerald-500" /> Protegido por Google reCAPTCHA v2
            </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
